<template>
    <template v-if="open">
        <nord-nav-group class="n-padding-i-m">
            <!-- Dropdown section -->
            <div class="notification-content" v-show="active_tab === 'client-tab'">
                <div v-if="showLoadingSpinner" class="text-center">
                    <div class="preloader"></div>
                </div>

                <!-- Messages section, messagesGrouped: object -->
                <template v-else>
                    <infinite-loading
                        :is-loading="messagesLoading"
                        :is-dropdown="false"
                        :has-finished-loading="!messagesAllLoaded"
                        @load-more="getMessages()"
                        class="infinite-loading"
                    >
                        <template v-if="hasMessages">
                            <nord-stack v-for="(messageGroup, date) in messagesGrouped" :key="date" gap="none">
                                <b class="notification-day-heading n-padding-is-s n-padding-bs-m n-color-nav-heading n-typescale-s truncate">
                                    {{ $filters.capitalize(formatDate(date)) }}
                                </b>
                                <message-preview-card
                                    v-for="message in messageGroup"
                                    :key="message.id"
                                    :message="message"
                                    @toggle-expand="toggleExpanded"
                                    @remove-all-messages-for-client="removeAllMessagesForClient"
                                    class="n-padding-i-m n-padding-b-s"
                                ></message-preview-card>
                            </nord-stack>
                        </template>

                        <div class="p-20 text-center notification-empty-state" v-if="isMessagesEmpty">
                            <img
                                :src="`/images/app/global/no_notifications${emptyStateImageSuffix}.png`"
                                :alt="trans('messages.no_new_messages')"
                                class="w-full"
                            >
                            <h5 class="m-b-0">{{ trans('messages.no_new_messages') }}</h5>
                        </div>
                    </infinite-loading>
                </template>
            </div>
        </nord-nav-group>
    </template>
</template>

<script>
import groupBy from 'lodash/groupBy';
import filter from 'lodash/filter';
import unionBy from 'lodash/unionBy';
import { http } from '@/system/http';
import MessagePreviewCard from '@/system/components/global/messages/MessagePreviewCard.vue';
import InfiniteLoading from '@/system/components/global/InfiniteLoading.vue';


export default {
    data() {
        return {
            active_tab: 'client-tab',
            messagesLoading: false,
            showLoadingSpinner: false,
            messagesAllLoaded: false,
            allConversations: [],
            messagesNextPageUrl: null,
        };
    },

    props: {
        open: Boolean,
    },

    components: {
        MessagePreviewCard,
        InfiniteLoading
    },

    mounted() {
        // Updates messages shown in the dropdown
        this.$event_bus.$on('remove-messages-from-message-dropdown', (clientId) => {
            this.removeAllMessagesForClient(clientId);
        });
    },

    methods: {
        getMessages(showLoading = false) {
            if (this.messagesLoading) return;

            this.messagesLoading = true;

            if (showLoading) this.showLoadingSpinner = true;

            const url = this.messagesNextPageUrl || '/app/client/messaging/all';

            http.get(url)
            .then(data => {
                this.handleMessagesResponse(data);
            })
            .catch(error => {
                this.messagesLoading = false;
                this.showLoadingSpinner = false;
                console.error("Error fetching conversations:", error);
                throw error; //re-throw error so that New Relic will pick it up.
            });
        },

        handleMessagesResponse(data) {
            this.messagesLoading = false;
            this.showLoadingSpinner = false;
            this.allConversations = unionBy(this.allConversations, data.data.data, "id");
            this.messagesNextPageUrl = data.data.links.next;

            if(!this.messagesAllLoaded && !data.data.links.next) {
                this.messagesAllLoaded = true;
            }

            this.allConversations.forEach(message => {
                this.processMessageLength(message);
            });
        },

        processMessageLength(message) {
            const isLongMessage = message.text.length > 290;
            message.isLongMessage = isLongMessage;
            message.isExpanded = message.isExpanded ?? false;

            message.truncatedItem = isLongMessage && !message.isExpanded
                ? message.text.substring(0, 290) + '...'
                : message.text;
        },

        toggleExpanded(message) {
            const index = this.allConversations.findIndex(m => m.id === message.id);
            if (index !== -1) {
                this.allConversations[index].isExpanded = !this.allConversations[index].isExpanded;
                this.allConversations[index] = {...this.allConversations[index]};
            }
        },

        removeAllMessagesForClient(clientId) {
            this.allConversations = this.allConversations.filter(item => item.client.id !== clientId);
        },

        formatDate(date) {
            let dateObj = moment(date);

            if (dateObj.isSame(moment(), 'd')) {
                return trans('app.today');
            }

            return dateObj.locale(moment_locale).format('dddd, LL');
        },
    },

    computed: {
        messagesGrouped() {
            // Loop through and group each conversation
            return groupBy(this.allConversations, item => {
                return moment(item.created_at.date).format('YYYY-MM-DD');
            });
        },

        hasMessages() {
            return this.allConversations.length > 0;
        },

        isMessagesEmpty() {
            return this.allConversations.length === 0;
        },

        emptyStateImageSuffix() {
            return this.$store.getters.isNordhealthBrand ? '_nh' : '';
        },
    },

    watch: {
        open(val, oldVal) {
            if (val) {
                this.messagesNextPageUrl = null;
                this.getMessages(true);
            }
        },
    },
}
</script>
